import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import {
  loadConsent,
  saveConsent,
  defaultConsent,
  injectGTM,
  injectMetaPixel,
} from "@/lib/tracking";

const CookieBanner = forwardRef(function CookieBanner({ gtmId, metaPixelId }, ref) {
  const [consent, setConsent] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prefs, setPrefs] = useState(defaultConsent);

  useEffect(() => {
    const existing = loadConsent();
    if (existing) {
      setConsent(existing);
      setPrefs(existing);
      activateTrackers(existing);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    openSettings: () => setSettingsOpen(true),
  }));

  function activateTrackers(c) {
    if (c.statistics && gtmId) injectGTM(gtmId);
    if (c.marketing && metaPixelId) injectMetaPixel(metaPixelId);
  }

  const acceptAll = () => {
    const c = { necessary: true, statistics: true, marketing: true };
    saveConsent(c);
    setConsent(c);
    activateTrackers(c);
    setSettingsOpen(false);
  };
  const rejectAll = () => {
    const c = { necessary: true, statistics: false, marketing: false };
    saveConsent(c);
    setConsent(c);
    setSettingsOpen(false);
  };
  const saveCustom = () => {
    const c = { ...prefs, necessary: true };
    saveConsent(c);
    setConsent(c);
    activateTrackers(c);
    setSettingsOpen(false);
  };

  const showBanner = !consent && !settingsOpen;

  if (consent && !settingsOpen) return null;

  return (
    <>
      {showBanner && (
        <div
          className="fixed inset-x-0 bottom-0 z-50 p-3 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-mfp-bg border-2 border-white/20 p-5 md:p-6 flex flex-col gap-4">
            <div>
              <div className="font-display text-lg md:text-2xl uppercase">
                Wir nutzen <span className="text-mfp-gold">Cookies</span>
              </div>
              <p className="mt-2 text-sm text-mfp-muted leading-relaxed">
                Diese Website verwendet Cookies und ähnliche Technologien. Einige sind
                notwendig, andere helfen uns dabei, die Performance zu messen und
                Marketing-Maßnahmen auszuspielen. Du entscheidest.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-3">
              <button
                type="button"
                onClick={rejectAll}
                className="mfp-btn bg-transparent border-2 border-white/30 text-white text-xs md:text-sm py-3 hover:bg-white/10 transition-colors"
                data-testid="cookie-reject"
              >
                Nur notwendige
              </button>
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                className="mfp-btn bg-transparent border-2 border-white text-white text-xs md:text-sm py-3 hover:bg-white hover:text-black transition-colors"
                data-testid="cookie-settings"
              >
                Einstellungen
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="mfp-btn bg-mfp-gold text-white text-xs md:text-sm py-3 hover:bg-mfp-goldHover transition-colors"
                data-testid="cookie-accept"
              >
                Alle akzeptieren
              </button>
            </div>
          </div>
        </div>
      )}

      {settingsOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-end md:items-center justify-center p-0 md:p-6"
          data-testid="cookie-modal"
        >
          <div className="w-full max-w-2xl bg-mfp-bg border-2 border-white/20 p-6 md:p-8 max-h-[90vh] overflow-y-auto">
            <div className="font-display text-2xl md:text-3xl uppercase mb-2">
              Cookie-<span className="text-mfp-gold">Einstellungen</span>
            </div>
            <p className="text-sm text-mfp-muted mb-6">
              Wähle gezielt aus, welche Kategorien du zulassen möchtest.
            </p>

            <div className="space-y-4">
              <ToggleRow
                label="Notwendig"
                description="Diese Cookies sind für den Betrieb der Seite erforderlich und können nicht deaktiviert werden."
                checked={true}
                disabled
                testid="cookie-cat-necessary"
              />
              <ToggleRow
                label="Statistik"
                description="Hilft uns zu verstehen, wie Besucher mit der Website interagieren (z.B. Google Tag Manager)."
                checked={prefs.statistics}
                onChange={(v) => setPrefs((p) => ({ ...p, statistics: v }))}
                testid="cookie-cat-statistics"
              />
              <ToggleRow
                label="Marketing"
                description="Ermöglicht personalisierte Werbung und Performance-Tracking (z.B. Meta Pixel)."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testid="cookie-cat-marketing"
              />
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={rejectAll}
                className="mfp-btn bg-transparent border-2 border-white/30 text-white text-xs md:text-sm py-3 hover:bg-white/10 transition-colors"
                data-testid="cookie-modal-reject"
              >
                Nur notwendige
              </button>
              <button
                type="button"
                onClick={saveCustom}
                className="mfp-btn bg-mfp-gold text-white text-xs md:text-sm py-3 hover:bg-mfp-goldHover transition-colors"
                data-testid="cookie-modal-save"
              >
                Auswahl speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

function ToggleRow({ label, description, checked, onChange, disabled, testid }) {
  return (
    <div className="border border-white/10 p-4 flex items-start gap-4" data-testid={testid}>
      <div className="flex-1">
        <div className="font-display uppercase text-sm tracking-widest">{label}</div>
        <p className="text-xs text-mfp-muted mt-1">{description}</p>
      </div>
      <label className="inline-flex items-center cursor-pointer select-none">
        <input
          type="checkbox"
          className="sr-only"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
        />
        <span
          className={`w-11 h-6 flex items-center p-0.5 transition-colors ${
            checked ? "bg-mfp-gold" : "bg-white/20"
          } ${disabled ? "opacity-60" : ""}`}
        >
          <span
            className={`block h-5 w-5 bg-white transition-transform ${
              checked ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </span>
      </label>
    </div>
  );
}

export default CookieBanner;
